import { TILE } from "./constants";

export interface Circle {
  x: number;
  z: number;
  radius: number;
}

export type SolidTest = (tileX: number, tileY: number) => boolean;

/** Tile containing a world coordinate. Tile t spans [t*TILE - TILE/2, t*TILE + TILE/2]. */
export function tileIndex(world: number): number {
  return Math.floor((world + TILE / 2) / TILE);
}

/** True if a circle overlaps any solid tile. */
export function hitsTerrain(x: number, z: number, radius: number, isSolid: SolidTest): boolean {
  const minX = tileIndex(x - radius);
  const maxX = tileIndex(x + radius);
  const minY = tileIndex(z - radius);
  const maxY = tileIndex(z + radius);

  for (let ty = minY; ty <= maxY; ty++) {
    for (let tx = minX; tx <= maxX; tx++) {
      if (!isSolid(tx, ty)) continue;
      // Closest point on the tile's box to the circle centre.
      const half = TILE / 2;
      const cx = Math.max(tx * TILE - half, Math.min(x, tx * TILE + half));
      const cz = Math.max(ty * TILE - half, Math.min(z, ty * TILE + half));
      const dx = x - cx;
      const dz = z - cz;
      if (dx * dx + dz * dz < radius * radius) return true;
    }
  }
  return false;
}

/** True if a circle overlaps any of the given obstacle circles. */
export function hitsCircles(x: number, z: number, radius: number, obstacles: readonly Circle[]): boolean {
  for (const other of obstacles) {
    const dx = x - other.x;
    const dz = z - other.z;
    const reach = radius + other.radius;
    if (dx * dx + dz * dz < reach * reach) return true;
  }
  return false;
}

/**
 * Moves a circle by (dx, dz), resolving each axis separately so that walking
 * into a wall at an angle slides along it instead of stopping dead.
 */
export function moveWithCollision(
  from: { x: number; z: number },
  dx: number,
  dz: number,
  radius: number,
  isSolid: SolidTest,
  obstacles: readonly Circle[] = [],
): { x: number; z: number; blocked: boolean } {
  let { x, z } = from;
  let blocked = false;

  if (dx !== 0) {
    const nx = x + dx;
    if (!hitsTerrain(nx, z, radius, isSolid) && !hitsCircles(nx, z, radius, obstacles)) x = nx;
    else blocked = true;
  }

  if (dz !== 0) {
    const nz = z + dz;
    if (!hitsTerrain(x, nz, radius, isSolid) && !hitsCircles(x, nz, radius, obstacles)) z = nz;
    else blocked = true;
  }

  return { x, z, blocked };
}
